import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card/Card';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useAccountProfile, useUpdateProfileMutation } from '@/hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { t } from '@/lib/i18n';

export function ProfilePage() {
  usePageHeader({ title: t('Profile'), breadcrumbs: [] });

  const { data: profile, isLoading: loading } = useAccountProfile();
  const updateProfileMutation = useUpdateProfileMutation();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Sync fullName state with profile data
  React.useEffect(() => {
    if (profile) {
      setFullName(profile.fullName || '');
    }
  }, [profile]);

  const refreshAuthUser = () => useAuthStore.getState().fetchMe(true);

  const handleSave = async () => {
    try {
      await updateProfileMutation.mutateAsync({ fullName });
      refreshAuthUser();
      try { useNotificationStore.getState().success(t('Profile updated')); } catch {}
    } catch (e) {
      try { useNotificationStore.getState().error(t('Failed to update profile')); } catch {}
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (file.size > 2 * 1024 * 1024) {
      useNotificationStore.getState().error(t('Image must be smaller than 2MB'));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      useNotificationStore.getState().error(t('Only JPEG, PNG, GIF, and WebP images are supported'));
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await apiFetch('/api/account/avatar', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Upload failed');
      }
      queryClient.invalidateQueries({ queryKey: ['account', 'profile'] });
      refreshAuthUser();
      useNotificationStore.getState().success(t('Avatar updated'));
    } catch (err: any) {
      useNotificationStore.getState().error(err.message || t('Failed to upload avatar'));
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true);
      const res = await apiFetch('/api/account/avatar', { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove avatar');
      queryClient.invalidateQueries({ queryKey: ['account', 'profile'] });
      refreshAuthUser();
      useNotificationStore.getState().success(t('Avatar removed'));
    } catch (err: any) {
      useNotificationStore.getState().error(err.message || t('Failed to remove avatar'));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (<div className="flex items-center justify-center h-40"><Spinner /></div>);

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Update your name and avatar. Email is managed under Security.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar
              size="lg"
              src={profile?.avatarUrl || undefined}
              initials={(profile?.fullName || profile?.email || 'U').charAt(0).toUpperCase()}
              name={profile?.fullName || profile?.email || 'User'}
            />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  loading={uploading}
                >
                  Upload photo
                </Button>
                {profile?.avatarUrl && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleRemoveAvatar}
                    disabled={uploading}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">JPEG, PNG, GIF, or WebP. Max 2MB.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Full name</label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" />
          </div>

        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} disabled={updateProfileMutation.isPending} loading={updateProfileMutation.isPending}>Save changes</Button>
        </CardFooter>
      </Card>
      </div>
    </PageContainer>
  );
}
